import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
    makeStyles,
    Paper,
    Grid,
    TextField,
    MenuItem,
    Button,
    Typography,
    IconButton,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Chip
} from "@material-ui/core";
import { format, addDays, startOfDay, endOfDay, isSameDay, parseISO, setHours, setMinutes, isWithinInterval, isBefore } from "date-fns";
import { ptBR } from "date-fns/locale";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import SettingsIcon from "@material-ui/icons/Settings";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import ViewListIcon from "@material-ui/icons/ViewList";
import ListIcon from "@material-ui/icons/List";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(2),
        overflowY: "auto",
        ...theme.scrollbarStyles,
    },
    calendarHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: theme.spacing(2)
    },
    slotCard: {
        padding: theme.spacing(1),
        margin: theme.spacing(1),
        minHeight: "80px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        cursor: "pointer",
        border: "1px solid #ccc",
        borderRadius: "4px",
        "&:hover": {
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
        }
    },
    slotFree: {
        backgroundColor: "#e8f5e9", // Light Green
        borderColor: "#c8e6c9"
    },
    slotBusy: {
        backgroundColor: "#e3f2fd", // Light Blue
        borderColor: "#bbdefb"
    },
    slotLunch: {
        backgroundColor: "#f5f5f5", // Grey
        borderColor: "#e0e0e0"
    },
    appointmentsList: {
        marginTop: theme.spacing(4)
    }
}));

import AppointmentModal from "../../components/AppointmentModal";
import AppointmentSettingsModal from "../../components/AppointmentSettingsModal";
import CalendarView from "../../components/CalendarView";

const Appointments = () => {
    const classes = useStyles();
    const history = useHistory();
    const [date, setDate] = useState(new Date());
    const [professionals, setProfessionals] = useState([]);
    const [selectedProfessionalId, setSelectedProfessionalId] = useState("");
    const [subSpecialties, setSubSpecialties] = useState([]);
    const [selectedSubSpecialtyId, setSelectedSubSpecialtyId] = useState("");
    const [appointments, setAppointments] = useState([]);
    const [slots, setSlots] = useState([]);

    // Modal State
    const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [settingsModalOpen, setSettingsModalOpen] = useState(false);
    const [appointmentsListModalOpen, setAppointmentsListModalOpen] = useState(false);

    // View Mode State
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'calendar'

    // Load Professionals
    useEffect(() => {
        const fetchProfessionals = async () => {
            try {
                const { data } = await api.get("/professionals");
                setProfessionals(data);
                if (data.length > 0) {
                    setSelectedProfessionalId(data[0].id);
                }
            } catch (err) {
                toastError(err);
            }
        };
        fetchProfessionals();
    }, []);

    // Load SubSpecialties (Services)
    useEffect(() => {
        const fetchSubSpecialties = async () => {
            try {
                const { data } = await api.get("/subspecialties");
                setSubSpecialties(data);
            } catch (err) {
                toastError(err);
            }
        };
        fetchSubSpecialties();
    }, []);

    // Load Appointments
    const fetchAppointments = async () => {
        if (!selectedProfessionalId) return;

        try {
            // Ensure full day range
            const start = startOfDay(date).toISOString();
            const end = endOfDay(date).toISOString();

            const { data } = await api.get("/appointments", {
                params: {
                    startDate: start,
                    endDate: end,
                    professionalId: selectedProfessionalId
                }
            });
            setAppointments(data);
        } catch (err) {
            toastError(err);
        }
    };

    useEffect(() => {
        fetchAppointments();
    }, [date, selectedProfessionalId]);

    // Check if date is a holiday
    const [holidays, setHolidays] = useState([]);
    const [isHolidayDate, setIsHolidayDate] = useState(false);

    useEffect(() => {
        const fetchHolidays = async () => {
            try {
                const { data } = await api.get("/holidays");
                setHolidays(data);
            } catch (err) {
                console.error("Error fetching holidays:", err);
            }
        };
        fetchHolidays();
    }, []);

    useEffect(() => {
        const checkHoliday = () => {
            const dateStr = format(date, "yyyy-MM-dd");
            const month = date.getMonth() + 1;
            const day = date.getDate();

            for (const holiday of holidays) {
                // Parse date correctly without timezone issues
                const [year, monthStr, dayStr] = holiday.date.split("-");
                const holidayMonth = parseInt(monthStr, 10);
                const holidayDay = parseInt(dayStr, 10);

                if (holiday.recurring) {
                    // Recurring: check only month and day
                    if (month === holidayMonth && day === holidayDay) {
                        setIsHolidayDate(true);
                        return;
                    }
                } else {
                    // Non-recurring: check exact date
                    if (holiday.date === dateStr) {
                        setIsHolidayDate(true);
                        return;
                    }
                }
            }
            setIsHolidayDate(false);
        };
        checkHoliday();
    }, [date, holidays]);

    // Generate Slots based on Professional Working Hours
    useEffect(() => {
        if (!selectedProfessionalId || professionals.length === 0) return;

        // If it's a holiday, don't show any slots
        if (isHolidayDate) {
            setSlots([]);
            return;
        }

        const professional = professionals.find(p => p.id === selectedProfessionalId);
        if (!professional) return;

        const dayOfWeekKey = format(date, "EEE").toLowerCase(); // mon, tue, etc.

        // PRIMEIRO: Verificar se o profissional trabalha neste dia
        if (professional.workDays && !professional.workDays.includes(dayOfWeekKey)) {
            // Profissional não trabalha neste dia
            setSlots([]);
            return;
        }

        // SEGUNDO: Verificar se há workingHours configurado para este dia
        if (professional.workingHours && !professional.workingHours[dayOfWeekKey]) {
            // Não há horário configurado para este dia
            setSlots([]);
            return;
        }

        // Default hours if logic fails or missing
        let startHour = "08:00";
        let endHour = "18:00";
        let lunchStart = "12:00";
        let lunchEnd = "13:00";

        if (professional.workingHours && professional.workingHours[dayOfWeekKey]) {
            startHour = professional.workingHours[dayOfWeekKey].start;
            endHour = professional.workingHours[dayOfWeekKey].end;
        }

        if (professional.lunchBreak && professional.lunchBreak[dayOfWeekKey]) {
            lunchStart = professional.lunchBreak[dayOfWeekKey].start;
            lunchEnd = professional.lunchBreak[dayOfWeekKey].end;
        }

        // Determine interval based on selected service
        let intervalMinutes = 60; // Default

        if (selectedSubSpecialtyId) {
            const subSpecialty = subSpecialties.find(s => s.id === parseInt(selectedSubSpecialtyId));
            if (subSpecialty && subSpecialty.duration) {
                intervalMinutes = subSpecialty.duration;
            }
        }

        // Generate Time Slots
        const [startH, startM] = startHour.split(":").map(Number);
        const [endH, endM] = endHour.split(":").map(Number);

        const generatedSlots = [];
        let current = setMinutes(setHours(date, startH), startM);
        const endDay = setMinutes(setHours(date, endH), endM);
        const now = new Date();

        while (current < endDay) {
            const nextSlot = new Date(current);
            nextSlot.setMinutes(current.getMinutes() + intervalMinutes); // DYNAMIC INTERVAL

            // Check if Lunch
            const slotTimeStr = format(current, "HH:mm");
            const isLunch = (slotTimeStr >= lunchStart && slotTimeStr < lunchEnd);
            const isPast = isBefore(current, now);

            // Check if Booked
            const appointment = appointments.find(app => {
                // Strict Check: Same Date AND Same Time
                // App Date comes as yyyy-MM-dd from backend usually, or ISO.
                // Let's rely on string comparison if possible or date-fns
                const appDate = parseISO(app.date);

                return isSameDay(appDate, current) &&
                    app.startTime.substring(0, 5) === slotTimeStr;
            });

            generatedSlots.push({
                time: slotTimeStr,
                lunch: isLunch,
                appointment: appointment,
                past: isPast
            });

            current = nextSlot;
        }
        setSlots(generatedSlots);

    }, [date, selectedProfessionalId, professionals, appointments, selectedSubSpecialtyId, subSpecialties, isHolidayDate]);

    const handleDateChange = (days) => {
        setDate(prev => addDays(prev, days));
    };

    const handleSlotClick = (slot) => {
        if (slot.lunch) return;
        if (slot.past && !slot.appointment) return; // Cannot book past slots

        if (slot.appointment) {
            // Open Modal in View/Edit Mode
            setSelectedSlot({
                ...slot.appointment,
                // Ensure date/time format compatibility
            });
            setAppointmentModalOpen(true);
        } else {
            // Open Modal for new Appointment
            setSelectedSlot({
                professionalId: selectedProfessionalId,
                subSpecialtyId: selectedSubSpecialtyId || "", // Pre-select service if chosen
                date: format(date, "yyyy-MM-dd"),
                startTime: slot.time,
                notes: ""
            });
            setAppointmentModalOpen(true);
        }
    };

    const handleConfirm = async (appointmentId) => {
        try {
            await api.patch(`/appointments/${appointmentId}/confirm`);
            toast.success("Agendamento confirmado!");
            fetchAppointments();
        } catch (err) {
            toastError(err);
        }
    };

    const handleCancelAppointment = async (appointmentId) => {
        try {
            await api.patch(`/appointments/${appointmentId}/cancel`);
            toast.success("Agendamento cancelado!");
            fetchAppointments();
        } catch (err) {
            toastError(err);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "CONFIRMED":
                return "primary";
            case "CANCELED":
                return "secondary";
            default:
                return "default";
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case "CONFIRMED":
                return "Confirmado";
            case "CANCELED":
                return "Cancelado";
            default:
                return "Pendente";
        }
    };

    return (
        <MainContainer>
            <MainHeader>
                <Title>{i18n.t("appointments.title")}</Title>
                <IconButton
                    onClick={() => setViewMode(viewMode === 'grid' ? 'calendar' : 'grid')}
                    color="primary"
                    title={viewMode === 'grid' ? 'Visualização Calendário' : 'Visualização Grade'}
                >
                    {viewMode === 'grid' ? <EventAvailableIcon /> : <ViewListIcon />}
                </IconButton>
                <IconButton
                    onClick={() => setAppointmentsListModalOpen(true)}
                    color="primary"
                    title="Lista de Agendamentos do Dia"
                >
                    <ListIcon />
                </IconButton>
                <IconButton onClick={() => setSettingsModalOpen(true)} color="primary">
                    <SettingsIcon />
                </IconButton>
            </MainHeader>

            {viewMode === 'grid' ? (
                // Visualização de Grade (Atual)
                <>
                    <Paper className={classes.mainPaper} variant="outlined">
                        <div className={classes.calendarHeader}>
                            {/* Calendar Shortcuts */}
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <IconButton onClick={() => handleDateChange(-1)}>
                                    <ArrowBackIosIcon />
                                </IconButton>
                                <Typography variant="h5" style={{ textTransform: "capitalize", margin: "0 10px" }}>
                                    {format(date, "dd/MM/yyyy (EEEE)", { locale: ptBR })}
                                </Typography>
                                <IconButton onClick={() => handleDateChange(1)}>
                                    <ArrowForwardIosIcon />
                                </IconButton>

                                <TextField
                                    type="date"
                                    value={format(date, "yyyy-MM-dd")}
                                    onChange={(e) => {
                                        if (e.target.value) setDate(parseISO(e.target.value))
                                    }}
                                    className={classes.textField}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    style={{ marginLeft: 20 }}
                                />
                            </div>

                            <TextField
                                select
                                variant="outlined"
                                label={i18n.t("professionals.title")}
                                value={selectedProfessionalId}
                                onChange={(e) => setSelectedProfessionalId(e.target.value)}
                                style={{ minWidth: 200 }}
                            >
                                {professionals.map((prof) => (
                                    <MenuItem key={prof.id} value={prof.id}>
                                        {prof.name}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                select
                                variant="outlined"
                                label="Serviço"
                                value={selectedSubSpecialtyId}
                                onChange={(e) => setSelectedSubSpecialtyId(e.target.value)}
                                style={{ minWidth: 200, marginLeft: 10 }}
                            >
                                <MenuItem value="">Todos os Serviços</MenuItem>
                                {subSpecialties.map((sub) => (
                                    <MenuItem key={sub.id} value={sub.id}>
                                        {sub.name} ({sub.duration} min)
                                    </MenuItem>
                                ))}
                            </TextField>
                        </div>

                        <Grid container spacing={2}>
                            {slots.length === 0 ? (
                                <Typography variant="body1" style={{ padding: 20 }}>
                                    Profissional não atende neste dia ou sem horários configurados.
                                </Typography>
                            ) : (
                                slots.map((slot, index) => (
                                    <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
                                        <Paper
                                            className={`${classes.slotCard} ${slot.lunch ? classes.slotLunch :
                                                slot.appointment ? classes.slotBusy :
                                                    slot.past ? classes.slotLunch :
                                                        classes.slotFree
                                                }`}
                                            elevation={1}
                                            onClick={() => handleSlotClick(slot)}
                                            style={slot.past && !slot.appointment ? { opacity: 0.6, cursor: "not-allowed" } : {}}
                                        >
                                            <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                                                {slot.time}
                                            </Typography>
                                            <Typography variant="body2">
                                                {slot.lunch ? i18n.t("appointments.status.lunch") :
                                                    slot.appointment ? slot.appointment.patientName || "Ocupado" :
                                                        slot.past ? "Encerrado" :
                                                            i18n.t("appointments.status.free")}
                                            </Typography>
                                            {slot.appointment && slot.appointment.patientName && (
                                                <Typography
                                                    variant="caption"
                                                    style={{
                                                        color: (slot.appointment.status || "PENDING") === "CONFIRMED" ? "green" : "red",
                                                        fontWeight: "bold",
                                                        fontSize: "0.65rem",
                                                        display: "block",
                                                        marginTop: "4px"
                                                    }}
                                                >
                                                    {(slot.appointment.status || "PENDING") === "CONFIRMED" ? "Confirmado" : "Não Confirmado"}
                                                </Typography>
                                            )}
                                        </Paper>
                                    </Grid>
                                ))
                            )}
                        </Grid>
                    </Paper>
                </>
            ) : (
                // Visualização de Calendário (Nova)
                <CalendarView
                    date={date}
                    onDateSelect={setDate}
                    appointments={appointments}
                    slots={slots}
                    onSlotClick={handleSlotClick}
                    professionals={professionals}
                    selectedProfessionalId={selectedProfessionalId}
                    setSelectedProfessionalId={setSelectedProfessionalId}
                    subSpecialties={subSpecialties}
                    selectedSubSpecialtyId={selectedSubSpecialtyId}
                    setSelectedSubSpecialtyId={setSelectedSubSpecialtyId}
                    onConfirm={handleConfirm}
                    onCancel={handleCancelAppointment}
                />
            )}

            <AppointmentModal
                open={appointmentModalOpen}
                onClose={() => setAppointmentModalOpen(false)}
                initialValues={selectedSlot}
                reload={fetchAppointments}
            />

            <AppointmentSettingsModal
                open={settingsModalOpen}
                onClose={() => setSettingsModalOpen(false)}
            />

            {/* Modal de Lista de Agendamentos */}
            <Dialog
                open={appointmentsListModalOpen}
                onClose={() => setAppointmentsListModalOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    style: {
                        boxShadow: '0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)'
                    }
                }}
            >
                <DialogTitle>
                    Agendamentos do Dia - {format(date, "dd/MM/yyyy (EEEE)", { locale: ptBR })}
                </DialogTitle>
                <DialogContent>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>Telefone</TableCell>
                                <TableCell>Horário</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {appointments.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        Nenhum agendamento para esta data
                                    </TableCell>
                                </TableRow>
                            ) : (
                                appointments.map((appt) => (
                                    <TableRow key={appt.id}>
                                        <TableCell>{appt.patientName}</TableCell>
                                        <TableCell>{appt.patientPhone}</TableCell>
                                        <TableCell>{appt.startTime?.substring(0, 5)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getStatusLabel(appt.status)}
                                                color={getStatusColor(appt.status)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            {appt.status === "PENDING" && (
                                                <>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleConfirm(appt.id)}
                                                        title="Confirmar"
                                                    >
                                                        <CheckCircleIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="secondary"
                                                        onClick={() => handleCancelAppointment(appt.id)}
                                                        title="Cancelar"
                                                    >
                                                        <CancelIcon />
                                                    </IconButton>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAppointmentsListModalOpen(false)} color="primary">
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        </MainContainer>
    );
};

export default Appointments;
